import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { reportsApi } from "@/lib/api/reports";
import { departmentsApi } from "@/lib/api/departments";
import { branchesApi } from "@/lib/api/branches";
import { employeesApi } from "@/lib/api/employees";
import { notificationsApi } from "@/lib/api/notifications";
import { leavesApi } from "@/lib/api/leaves";
import { expenseApi } from "@/lib/api/expense";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { Spinner } from "@/components/ui/Spinner";
import { formatMoneyCompact, formatNumber, formatPeriod } from "@/lib/utils/format";
import { can, useAuthStore } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";
import { ActionCenter } from "./ActionCenter";
import { UpcomingEvents } from "./UpcomingEvents";
import { buildHiringTrend } from "./hiring-trend";
import { RecentActivities } from "./RecentActivities";
import {
  DashboardHeader,
  EmployeeListCard,
  EmployeeStatusCard,
  GeoSnapshotCard,
  KpiCard,
  LeaveApplicationsCard,
  PayrollSnapshotCard,
  PulseCard,
  WorkforceInsightsCard,
} from "./sections";
import { computeTrend, startOfMonthISO } from "./sections-utils";

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.reports.dashboard,
    queryFn: reportsApi.dashboard,
  });

  // Feature Toggle Management: a disabled module's widget is hidden here the
  // same way its nav item is (Sidebar.tsx) and its route is
  // (RequireFeature) - see lib/features.ts for why this fails open on a
  // still-loading/unrecognised key rather than flashing every tile empty on
  // first paint.
  const attendanceEnabled = useFeatureEnabled("attendance_management");
  const payrollEnabled = useFeatureEnabled("payroll_management");
  const leaveEnabled = useFeatureEnabled("leave_management");
  const employeeMgmtEnabled = useFeatureEnabled("employee_management");
  const branchesEnabled = useFeatureEnabled("branch_management");
  const expenseEnabled = useFeatureEnabled("expense_management");
  const geoFencingEnabled = useFeatureEnabled("geo_fencing");
  // Super Admin must never see tenant employee location data, even as an
  // aggregate count (section 13: "Do not expose employee location
  // information on platform-level Super Admin dashboards") - excluded here
  // the same way the Payroll/Recent Activities cards above already exclude
  // Super Admin from data that isn't theirs to see.
  const canSeeGeoFencing = geoFencingEnabled && !user?.is_super_admin && can("report.read");
  const {
    data: geoSnapshot,
    isError: geoSnapshotFailed,
    refetch: refetchGeoSnapshot,
  } = useQuery({
    queryKey: qk.reports.geoFenceSummary,
    queryFn: () => reportsApi.geoFenceSummary(),
    enabled: canSeeGeoFencing,
  });

  // Every figure below is real, but two of them (New Joinees, the hiring
  // trend chart) are computed client-side from the existing paginated
  // /employees list (sorted by joining_date, on-roll only) rather than
  // fetched pre-aggregated - this system has no month-by-month headcount
  // snapshot or "joined this week" counter endpoint, and inventing one was
  // out of scope for a presentation-layer redesign. Bounded to the 100 most
  // recently joined on-roll employees, which comfortably covers any
  // realistic month's hiring for this table's page-size class; a tenant
  // that hires more than 100 people in a single month would undercount here.
  // Both queries below hit employee.read-gated endpoints (/employees,
  // /departments) - the feature toggle alone isn't enough, the same as
  // canSeeBranches/canSeeGeoFencing just below. Without the permission half,
  // any role that holds no employee.read (Employee is the common case) got a
  // background 403 on every dashboard load and a permanently empty/zero
  // Hiring Trend, Departments and New Joinees widget - a page they have no
  // reason to see anyway, since it's whole-org headcount analytics.
  const canSeeEmployeeDirectory = employeeMgmtEnabled && can("employee.read");
  const {
    data: recentJoiners,
    isError: recentJoinersFailed,
    refetch: refetchRecentJoiners,
  } = useQuery({
    queryKey: qk.employees.list({ sort: "joining_date", order: "desc", on_roll: true, per_page: 100 }),
    queryFn: () => employeesApi.list({ sort: "joining_date", order: "desc", on_roll: true, per_page: 100 }),
    enabled: canSeeEmployeeDirectory,
  });
  const {
    data: departments,
    isError: departmentsFailed,
    refetch: refetchDepartments,
  } = useQuery({
    queryKey: qk.departments.list({ per_page: 100 }),
    queryFn: () => departmentsApi.list({ per_page: 100 }),
    enabled: canSeeEmployeeDirectory,
  });
  // A couple of real numbers next to the Departments donut, not a new chart -
  // see project_dashboard_reference_restructure's "don't overbuild" note.
  const {
    data: departmentSummary,
    isError: departmentSummaryFailed,
    refetch: refetchDepartmentSummary,
  } = useQuery({
    queryKey: qk.departments.summary,
    queryFn: () => departmentsApi.summary(),
    enabled: canSeeEmployeeDirectory,
  });
  const canSeeBranches = branchesEnabled && can("branch.read");
  const {
    data: branches,
    isError: branchesFailed,
    refetch: refetchBranches,
  } = useQuery({
    queryKey: qk.branches.list({ per_page: 100 }),
    queryFn: () => branchesApi.list({ per_page: 100 }),
    enabled: canSeeBranches,
  });
  const {
    data: activity,
    isError: activityFailed,
    refetch: refetchActivity,
  } = useQuery({
    queryKey: qk.notifications.list({ per_page: 6 }),
    queryFn: () => notificationsApi.list({ per_page: 6 }),
    // Super Admin has no in-app notification feed of their own on this
    // surface (NotificationBell already hides for them in the Topbar) - see
    // that component's own note.
    enabled: !user?.is_super_admin,
  });

  // KPI row additions - Employee Status breakdown, recent Leave Applications
  // and Total Expense all reuse endpoints the Module Dashboards wave already
  // built this session (EmployeeManagementDashboardPage/LeaveDashboardPage/
  // ExpenseDashboardPage), rather than inventing new aggregates here.
  const {
    data: headcountByStatus,
    isError: headcountByStatusFailed,
    refetch: refetchHeadcountByStatus,
  } = useQuery({
    queryKey: qk.employees.headcount,
    queryFn: employeesApi.headcount,
    enabled: canSeeEmployeeDirectory,
  });
  const canSeeLeaveApplications = leaveEnabled && can("leave.read");
  const {
    data: recentLeaves,
    isError: recentLeavesFailed,
    refetch: refetchRecentLeaves,
  } = useQuery({
    queryKey: qk.leaves.list({ per_page: 6 }),
    queryFn: () => leavesApi.list({ per_page: 6 }),
    enabled: canSeeLeaveApplications,
  });
  const canSeeExpenseSummary = expenseEnabled && can("expense.view_reports");
  const {
    data: expenseSummary,
    isError: expenseSummaryFailed,
    refetch: refetchExpenseSummary,
  } = useQuery({
    queryKey: qk.expense.summary,
    queryFn: expenseApi.summary,
    enabled: canSeeExpenseSummary,
  });

  // Aggregate failure/retry surface for every secondary/below-the-fold
  // widget above - each of them otherwise renders its own "no data yet"
  // empty state on a genuine fetch failure too (a misleading zero/empty
  // value, not an honest "nothing here"). Mirrors ActionCenter's/
  // UpcomingEvents' own failedSources + retry banner pattern rather than
  // rewriting every individual widget's empty-state branch to distinguish
  // failure from emptiness on its own - one visible, actionable banner
  // instead of N silent ones. Deliberately not a toast (see
  // MutationCache's own reasoning for why background query failures don't
  // toast) and doesn't touch the page's main query (isLoading/error above),
  // which already has its own full-page loading/error handling.
  const failedWidgets = [
    { label: "New Joinees", failed: recentJoinersFailed, retry: refetchRecentJoiners },
    { label: "Departments", failed: departmentsFailed, retry: refetchDepartments },
    { label: "Department Summary", failed: departmentSummaryFailed, retry: refetchDepartmentSummary },
    { label: "Branches", failed: branchesFailed, retry: refetchBranches },
    { label: "Recent Activity", failed: activityFailed, retry: refetchActivity },
    { label: "Employee Status", failed: headcountByStatusFailed, retry: refetchHeadcountByStatus },
    { label: "Recent Leave Requests", failed: recentLeavesFailed, retry: refetchRecentLeaves },
    { label: "Total Expense", failed: expenseSummaryFailed, retry: refetchExpenseSummary },
    { label: "Geo-Fencing", failed: geoSnapshotFailed, retry: refetchGeoSnapshot },
  ].filter((w) => w.failed);

  const hiringTrend = useMemo(() => buildHiringTrend(recentJoiners?.items ?? []), [recentJoiners]);

  const insightsTabs = useMemo(() => {
    const tabs: { key: string; label: string }[] = [];
    if (canSeeEmployeeDirectory) tabs.push({ key: "hiring", label: "Hiring Trend" });
    if (canSeeEmployeeDirectory) tabs.push({ key: "departments", label: "Departments" });
    if (attendanceEnabled) tabs.push({ key: "attendance", label: "Attendance" });
    if (canSeeBranches) tabs.push({ key: "branches", label: "Branches" });
    return tabs;
  }, [canSeeEmployeeDirectory, attendanceEnabled, canSeeBranches]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-6 w-6 text-primary-600" />
      </div>
    );
  }
  if (error) return <ErrorMessage error={error} />;
  if (!data) return null;

  const { current, previous } = data;
  const newJoineesThisMonth = recentJoiners?.items.filter((e) => e.joining_date >= startOfMonthISO()).length ?? 0;
  const presentCount = current.attendance_mix?.present ?? 0;
  const onLeaveCount = current.attendance_mix?.leave ?? 0;
  // Headcount is the only KPI with a real, honest period-over-period trend:
  // it's a point-in-time count, not a cumulative monthly counter, so
  // comparing it across periods is a fair like-for-like comparison. See the
  // "Present this month" KpiCard below for why that one deliberately has no
  // trend pill.
  const headcountTrend = computeTrend(current.headcount, previous?.headcount);


  return (
    <div>
      <DashboardHeader period={current.period} />

      {failedWidgets.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-2.5 text-xs text-amber-700">
          <span>
            Some dashboard data couldn't load: {failedWidgets.map((w) => w.label).join(", ")}. The figures shown for
            {failedWidgets.length === 1 ? " it" : " them"} may be out of date or incomplete.
          </span>
          <button
            type="button"
            onClick={() => failedWidgets.forEach((w) => w.retry())}
            className="font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* KPI card row - icon in a soft colored circle, a trend pill only
          where real period-over-period data exists (Employees/Present), a
          real mini-sparkline only where real trend series data exists
          (Expense's monthly_trend) - no fabricated trends or decoration. */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Total Employees" value={formatNumber(current.headcount)} hint="on-roll" icon="users" tone="primary" trend={headcountTrend} to="/employees" />
        {attendanceEnabled && (
          <KpiCard
            // "Present this month" (month-to-date, matching what
            // current.attendance_mix.present actually measures), not
            // "Present Today" as the reference mockup labels it - this app
            // has no single-day attendance snapshot wired to the dashboard,
            // and mislabeling a cumulative monthly count as "today" would
            // misrepresent real data. Same reasoning drops the trend pill:
            // comparing a still-in-progress current month's cumulative count
            // against a completed previous month produces a large, alarming
            // swing that reflects "we're mid-month," not an actual
            // attendance change.
            label="Present this month"
            to="/attendance"
            value={formatNumber(presentCount)}
            hint={current.headcount ? `${((presentCount / current.headcount) * 100).toFixed(0)}% of workforce` : undefined}
            icon="check-circle"
            tone="emerald"
          />
        )}
        {canSeeExpenseSummary && expenseSummary && (
          <KpiCard
            label="Total Expense"
            to="/expenses/dashboard"
            value={formatMoneyCompact(expenseSummary.total_amount)}
            hint={`${expenseSummary.total_claims} claim${expenseSummary.total_claims === 1 ? "" : "s"}`}
            icon="credit-card"
            tone="accent"
            sparkline={expenseSummary.monthly_trend.map((p) => Number(p.amount))}
          />
        )}
        {payrollEnabled && can("payroll.read") && previous?.payroll && (
          <KpiCard
            label="Total Payroll"
            to="/payroll/runs"
            value={formatMoneyCompact(previous.payroll.total_gross)}
            hint={formatPeriod(previous.period)}
            icon="wallet"
            tone="indigo"
          />
        )}
      </div>

      {/* Analytics - every real, already-built insight this app has:
          the tabbed Hiring Trend/Departments/Attendance/Branches chart +
          Leave Applications, Employee Status + Employee List (Workforce
          Overview), the compact secondary metrics row, and the Payroll/
          Geo-Fencing snapshots (Payroll Overview). Nothing here is new -
          this is the prior dashboard's own content, relabelled and grouped
          under one heading per the redesign's Header -> KPI -> Analytics ->
          Action Center -> Upcoming Events/Recent Activity hierarchy, so
          nothing that used to be on this page silently disappears. */}
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Analytics</p>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {insightsTabs.length > 0 && (
          <WorkforceInsightsCard
            className="lg:col-span-2"
            tabs={insightsTabs}
            hiringTrend={hiringTrend}
            departments={departments?.items ?? []}
            departmentSummary={departmentSummary}
            attendanceMix={current.attendance_mix ?? {}}
            branches={branches?.items ?? []}
          />
        )}
        {canSeeLeaveApplications && (
          <LeaveApplicationsCard items={recentLeaves?.items} className={insightsTabs.length > 0 ? undefined : "lg:col-span-3"} />
        )}
      </div>


      {/* Employee Status (real by-status headcount breakdown) + Employee
          List (compact preview of the same recentJoiners page the Hiring
          Trend chart already fetched, no duplicate call) - Workforce
          Overview. */}
      {canSeeEmployeeDirectory && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <EmployeeStatusCard byStatus={headcountByStatus?.by_status} />
          <EmployeeListCard items={recentJoiners?.items} className="lg:col-span-2" />
        </div>
      )}


      {/* Compact secondary metrics - the small workforce counters
          (present/on-leave/new-joinees as %, working days, holidays,
          overtime) that used to headline the old "Workforce Pulse" panel
          still matter, just demoted below the new KPI cards rather than
          dropped - see feedback_dont_silently_drop_existing_dashboard_stats. */}
      <div className="mb-6">
        <PulseCard
          stats={[
            ...(attendanceEnabled || leaveEnabled
              ? [
                  {
                    key: "on-leave",
                    label: "On leave",
                    value: formatNumber(onLeaveCount),
                    hint: current.headcount ? `${((onLeaveCount / current.headcount) * 100).toFixed(1)}%` : undefined,
                  },
                ]
              : []),
            ...(canSeeEmployeeDirectory ? [{ key: "new-joinees", label: "New joinees", value: formatNumber(newJoineesThisMonth), hint: "this month" }] : []),
            { key: "working-days", label: "Working days", value: formatNumber(current.working_days) },
            { key: "holidays", label: "Holidays", value: formatNumber(current.holidays), hint: "this month" },
            ...(attendanceEnabled ? [{ key: "overtime", label: "Overtime hours", value: formatNumber(current.total_overtime_hours, 1) }] : []),
          ]}
        />
      </div>


      {/* Payroll snapshot (Payroll Overview) + Geo-Fencing snapshot - the
          pending/missing payroll state and the outside-fence alert are
          surfaced as real actions in Action Center below; these stay as
          smaller reference panels for the actual figures. */}
      {payrollEnabled && previous?.payroll && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <PayrollSnapshotCard payroll={previous.payroll} period={previous.period} />
          {canSeeGeoFencing && geoSnapshot && <GeoSnapshotCard snapshot={geoSnapshot} />}
        </div>
      )}

      {canSeeGeoFencing && geoSnapshot && !(payrollEnabled && previous?.payroll) && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <GeoSnapshotCard snapshot={geoSnapshot} interactive />
        </div>
      )}


      {/* Action Center - dynamic, RBAC-aware "what needs me right now",
          replacing the old static "Needs your attention" card. Owns all of
          its own queries; see ActionCenter.tsx. */}
      <ActionCenter />

      {/* Upcoming Events (new - birthdays, anniversaries, holidays,
          interviews, performance deadlines, onboarding dates, each
          permission-gated independently) + Recent Activity (kept, moved
          here from its old slot next to the compact metrics row) - the
          "what's coming next" close of the Header -> KPI -> Analytics ->
          Action Center -> Upcoming Events/Recent Activity hierarchy. */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <UpcomingEvents />
        {!user?.is_super_admin && (
          <Card interactive>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardBody className="pt-0">
              <RecentActivities items={activity?.items ?? []} />
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}
